import { useQuery, UseQueryResult } from 'react-query';
import { RpColumn, RpColumnRenderType } from '../../models/RpColumn';
import { EsField, RpColumnDto } from '../../services/dtos/riskProducts/rpColumn.dto';
import { fetchRpColumns } from '../../services/riskProducts/riskProducts.service';
import { queryKeys } from '../queryKeyFactory';

const CUSTOM_RENDER_TYPE_BY_ES_FIELD: Record<string, EsField[]> = {
  [RpColumnRenderType.COMPANY_LINK]: ['display_name'],
  [RpColumnRenderType.DATE]: ['calc_date', 'fund_date', 'data_date'],
  [RpColumnRenderType.COLORED]: [
    'cqs_volatility',
    'credit_risk_level',
    'cqs_trend_1y',
    'cqs_trend_3y',
    'cqs_outlook',
    'credit_risk_trend_6m',
    'fas',
  ],
  [RpColumnRenderType.METRIC_CHANGE_INDICATOR]: ['cqs', 'cre_1y'],
  [RpColumnRenderType.ROUNDED]: [
    'cqs_change_3m',
    'cqs_change_1y',
    'cqs_previous',
    'cre_change_1w',
    'cre_change_1m',
    'cre_change_ytd',
    'cre_percentile',
    'cre_sector_1_pct',
    'gross_debt_to_ebitda',
    'net_debt_to_ebitda',
    'fscore',
    'fscore_previous',
    'country_risk_score',
    'cds_spread',
    'cds_spread_1w_change',
    'cds_spread_1m_change',
    'cds_spread_ytd_change',
    'implied_spread',
    'implied_spread_1w_change',
    'implied_spread_1m_change',
    'implied_spread_ytd_change',
    'nrv_value',
    'nrv_weeks_since_last_change',
  ],
  [RpColumnRenderType.PERCENTAGE]: [
    'escore',
    'cre_3y',
    'cre_5y',
    'cre_previous',
    'mkt_leverage',
    'market_leverage',
    'ebitda_margin',
  ],
};

const getRenderType = (columnDto: RpColumnDto) => {
  const entry = Object.entries(CUSTOM_RENDER_TYPE_BY_ES_FIELD).find(([, fields]) =>
    fields.includes(columnDto.es_field)
  );
  return entry ? (entry[0] as RpColumnRenderType) : RpColumnRenderType.DEFAULT;
};

const getMeta = (columnDto: RpColumnDto) => {
  switch (columnDto.es_field) {
    case 'cre_1y':
      return { showPercents: true, previousField: 'cre_previous' };
    case 'cqs':
      return { showPercents: false, previousField: 'cqs_previous' };
    case 'cre_previous':
    case 'cre_3y':
    case 'cre_5y':
    case 'escore':
    case 'country_risk_score':
      return { precision: 2 };
    case 'mkt_leverage':
    case 'market_leverage':
    case 'ebitda_margin':
      return { precision: 1 };
    case 'gross_debt_to_ebitda':
    case 'net_debt_to_ebitda':
      return { precision: 1, suffix: 'x' };
    case 'nrv_value':
      return { precision: 3 };
    case 'cqs_change_3m':
    case 'cqs_change_1y':
    case 'cqs_previous':
    case 'fscore':
    case 'fscore_previous':
    case 'cre_change_1w':
    case 'cre_change_1m':
    case 'cre_change_ytd':
    case 'cre_percentile':
    case 'cre_sector_1_pct':
    case 'cds_spread':
    case 'cds_spread_1w_change':
    case 'cds_spread_1m_change':
    case 'cds_spread_ytd_change':
    case 'implied_spread':
    case 'implied_spread_1w_change':
    case 'implied_spread_1m_change':
    case 'implied_spread_ytd_change':
    case 'nrv_weeks_since_last_change':
      return { precision: 0 };
    default:
      return undefined;
  }
};

const useFetchRpColumns = (disabled?: boolean): UseQueryResult<RpColumn[], Error> => {
  return useQuery<RpColumn[], Error>(
    queryKeys.fetchRiskProducts.columns,
    async () => {
      const data = await fetchRpColumns();
      return data.map((column) => ({
        ...column,
        renderType: getRenderType(column),
        meta: getMeta(column),
      }));
    },
    {
      cacheTime: Infinity,
      enabled: !disabled,
    }
  );
};

export default useFetchRpColumns;
